import { Form, Button, Row, Col, Card } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import { message } from "antd";
import {
  useGetFAQByIdQuery,
  useUpdateFAQMutation,
} from "../../../redux/api/FAQApi/FAQApi";
import { FaEdit, FaSave, FaArrowLeft } from "react-icons/fa";
import "../Services/Services.css";
import LoaderSpinner from "../../../Components/LoaderSpinner";
import { useFormik } from "formik";
import * as Yup from "yup";

const EditFAQ = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data: faqData, isLoading: isFetching } = useGetFAQByIdQuery(id);
  const [updateFAQ, { isLoading: isUpdating }] = useUpdateFAQMutation();

  const validationSchema = Yup.object().shape({
    question: Yup.string().trim().required("Question is required"),
    answer: Yup.string().trim().required("Answer is required"),
    display_order: Yup.number(),
    status: Yup.boolean(),
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      question: faqData?.question || "",
      answer: faqData?.answer || "",
      display_order: faqData?.display_order || 0,
      status: faqData?.status ?? true,
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        await updateFAQ({ id, ...values }).unwrap();
        message.success("FAQ updated successfully!");
        setTimeout(() => navigate("/admin/faq"), 2000);
      } catch (error) {
        message.error(
          error.data?.error || error.message || "Failed to update FAQ",
        );
      }
    },
  });

  if (isFetching || isUpdating) {
    return (
      <div
        className="d-flex justify-content-center align-items-center bg-light"
        style={{ height: "100vh" }}
      >
        <LoaderSpinner />
      </div>
    );
  }

  return (
    <div className="container py-4 fade-in">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="fw-bold text-dark mb-1">
            <FaEdit className="text-primary me-2" />
            Edit FAQ
          </h2>
          <p className="text-muted mb-0">
            Modify the details of this FAQ entry
          </p>
        </div>
        <Button
          variant="outline-secondary"
          className="d-flex align-items-center gap-2 shadow-sm rounded-pill px-4"
          onClick={() => navigate("/admin/faq")}
        >
          <FaArrowLeft /> Back to List
        </Button>
      </div>

      <Form onSubmit={formik.handleSubmit}>
        <Card className="border-0 overflow-hidden">
          <Card.Body className="p-4 p-md-5">
            <h5 className="mb-4 text-secondary border-start border-4 border-primary ps-3">
              FAQ Details
            </h5>
            <Row className="g-4">
              <Col md={12}>
                <Form.Group>
                  <Form.Label className="fw-semibold">
                    Question <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="question"
                    {...formik.getFieldProps("question")}
                    isInvalid={
                      formik.touched.question && !!formik.errors.question
                    }
                  />
                  <Form.Control.Feedback type="invalid">
                    {formik.errors.question}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>

              <Col md={12}>
                <Form.Group>
                  <Form.Label className="fw-semibold">
                    Answer <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    name="answer"
                    {...formik.getFieldProps("answer")}
                    isInvalid={formik.touched.answer && !!formik.errors.answer}
                  />
                  <Form.Control.Feedback type="invalid">
                    {formik.errors.answer}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-semibold">Display Order</Form.Label>
                  <Form.Control
                    type="number"
                    name="display_order"
                    {...formik.getFieldProps("display_order")}
                  />
                </Form.Group>
              </Col>

              <Col md={6} className="d-flex align-items-center mt-md-auto mt-4">
                <Form.Group>
                  <Form.Check
                    type="switch"
                    id="faq-status-edit"
                    label="Active / Published"
                    name="status"
                    checked={formik.values.status}
                    onChange={formik.handleChange}
                    className="fw-bold text-primary"
                  />
                </Form.Group>
              </Col>
            </Row>

            <div className="d-flex justify-content-end gap-3 mt-5">
              <Button
                variant="light"
                className="rounded-pill px-4"
                onClick={() => navigate("/admin/faq")}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                type="submit"
                className="rounded-pill px-5 shadow-sm d-flex align-items-center gap-2"
              >
                <FaSave /> Update FAQ
              </Button>
            </div>
          </Card.Body>
        </Card>
      </Form>
    </div>
  );
};

export default EditFAQ;
